<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConvexQuery, useConvexMutation } from 'convex-vue';
import { api } from '../../convex/_generated/api';

const { t } = useI18n();

const props = defineProps<{
  modelValue: boolean;
  token: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  accepted: [workshopId: string, slug: string];
}>();

const isProcessing = ref(false);
const error = ref('');

const { data: inviteData, isPending } = useConvexQuery(api.inviteLinks.validateToken, () => {
  if (!props.token || !props.modelValue) {
    return { token: '' };
  }
  return { token: props.token };
});

const useInvite = useConvexMutation(api.inviteLinks.useInvite);

const isValid = computed(() => inviteData.value?.valid === true);
const workshopName = computed(() => inviteData.value?.workshopName || '');
const role = computed(() => inviteData.value?.role || '');
const currentRole = computed(() => inviteData.value?.currentRole || null);
const status = computed(() => inviteData.value?.status || 'new');

const isPromotion = computed(() => status.value === 'promotion');
const isSameRole = computed(() => status.value === 'same');
const isDowngrade = computed(() => status.value === 'downgrade');

const roleLabels: Record<string, string> = {
  owner: t('roles.owner'),
  member: t('roles.member'),
  reader: t('roles.reader'),
};

const roleDescriptions: Record<string, string> = {
  owner: t('roles.ownerDescription'),
  member: t('roles.memberDescription'),
  reader: t('roles.readerDescription'),
};

const roleColors: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-800 border-purple-300',
  member: 'bg-blue-100 text-blue-800 border-blue-300',
  reader: 'bg-green-100 text-green-800 border-green-300',
};

const handleAccept = async () => {
  if (!props.token) return;

  isProcessing.value = true;
  error.value = '';

  try {
    const result = await useInvite.mutate({ token: props.token });
    if (result.success) {
      emit('accepted', result.workshopId, result.slug || '');
      emit('update:modelValue', false);
    } else {
      error.value = 'Failed to join workshop';
    }
  } catch (e: any) {
    error.value = e.message || t('invite.failedToJoin');
  } finally {
    isProcessing.value = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="modelValue"
            class="invitation-card rounded-xl max-w-md w-full p-8 shadow-2xl relative"
            @click.stop
          >
            <!-- Paper texture overlay -->
            <div
              class="absolute inset-0 opacity-30 pointer-events-none rounded-xl"
              style="
                background-image: url(&quot;data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E&quot;);
              "
            ></div>

            <div v-if="isPending" class="text-center py-8 relative z-10">
              <div
                class="inline-block w-8 h-8 border-3 border-yellow-600/30 border-t-yellow-600 rounded-full animate-spin mb-4"
              ></div>
              <p class="text-yellow-800">{{ $t('invite.loading') }}</p>
            </div>

            <div v-else-if="!isValid" class="text-center py-8 relative z-10">
              <div
                class="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center border-2 border-red-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-2">
                {{ $t('invite.invalidTitle') }}
              </h3>
              <p class="text-gray-600">
                {{ $t('invite.invalidDescription') }}
              </p>
              <button
                @click="$emit('update:modelValue', false)"
                class="mt-6 px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium cursor-pointer"
              >
                {{ $t('common.close') }}
              </button>
            </div>

            <div v-else class="relative z-10">
              <!-- Already has same or higher role - show info only -->
              <div v-if="isSameRole || isDowngrade" class="text-center">
                <div
                  class="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-400"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-8 w-8 text-blue-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-800 mb-2">
                  {{ $t('invite.alreadyMember') }}
                </h3>
                <p class="text-gray-600 mb-4">
                  {{
                    $t(
                      `invite.already${currentRole === 'owner' ? 'Owner' : currentRole === 'member' ? 'MemberRole' : 'Reader'}`
                    )
                  }}
                </p>
                <div class="bg-white/60 rounded-lg p-4 mb-6 border border-blue-400/50 shadow-inner">
                  <h4 class="text-lg font-bold text-gray-800 mb-2">{{ workshopName }}</h4>
                  <span
                    :class="
                      roleColors[currentRole || ''] || 'bg-gray-100 text-gray-800 border-gray-300'
                    "
                    class="px-3 py-1 rounded-full text-sm font-medium border"
                  >
                    {{ roleLabels[currentRole || ''] || currentRole }}
                  </span>
                </div>
                <button
                  @click="$emit('update:modelValue', false)"
                  class="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium cursor-pointer"
                >
                  {{ $t('common.close') }}
                </button>
              </div>

              <!-- Promotion available or new user - show accept only -->
              <div v-else>
                <div class="text-center mb-6">
                  <div
                    class="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center border-2 border-yellow-400"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-8 w-8 text-yellow-700"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 class="text-xl font-semibold text-gray-800 mb-1">
                    {{ $t('invite.workshopInvitation') }}
                  </h3>
                  <p class="text-gray-600">{{ $t('invite.invitedToJoin') }}</p>
                </div>

                <div
                  class="bg-white/60 rounded-lg p-5 mb-6 border border-yellow-400/50 shadow-inner"
                >
                  <h4 class="text-lg font-bold text-gray-800 mb-3 text-center">
                    {{ workshopName }}
                  </h4>
                  <div class="flex items-center justify-center gap-2 mb-3">
                    <span
                      :class="roleColors[role] || 'bg-gray-100 text-gray-800 border-gray-300'"
                      class="px-3 py-1 rounded-full text-sm font-medium border"
                    >
                      {{ roleLabels[role] || role }}
                    </span>
                  </div>
                  <p class="text-gray-600 text-sm text-center">{{ roleDescriptions[role] }}</p>

                  <!-- Promotion notice -->
                  <div
                    v-if="isPromotion && currentRole"
                    class="mt-3 p-2 bg-green-100 border border-green-300 rounded text-center"
                  >
                    <p class="text-green-700 text-sm">
                      {{
                        $t('invite.promotionAvailable', {
                          currentRole: roleLabels[currentRole],
                          newRole: roleLabels[role],
                        })
                      }}
                    </p>
                  </div>
                </div>

                <div v-if="error" class="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                  <p class="text-red-700 text-sm">{{ error }}</p>
                </div>

                <div class="flex gap-4 justify-center">
                  <!-- Accept button only -->
                  <button
                    @click="handleAccept"
                    :disabled="isProcessing"
                    class="wax-seal accept"
                    title="Accept"
                  >
                    <svg
                      v-if="!isProcessing"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span
                      v-else
                      class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
                    ></span>
                  </button>
                </div>

                <p class="text-center text-gray-500 text-sm mt-4">
                  {{ $t('invite.clickToAccept') }}
                </p>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
